import { IndeterminateProgressBar } from 'cdk-radial';
import { isArray, isEmpty } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { voidAndReprintChecks } from 'adapters/services';
import { CheckInfoType } from 'adapters/types';
import { EVENT_NAME } from 'common/constants';
import { Toast } from 'components';
import { useAmplitude } from 'components/AmplitudeProvider';
import { useClient } from 'components/ClientProvider';
import { getCompanyId, getStoreId } from 'components/StoreSelect/slice';
import {
  useFlagValue,
  useFlags,
  useUpdateInvoiceLockStatus,
  useVoidChecks
} from 'hooks';
import {
  useDeleteApproval,
  useFetchCashInBankInterCompany,
  useFetchPrintersWithCheckNo
} from 'hooks/useClientDataServices';
import { getStoreIdByCompanyId } from 'pages/AuthorizePaymentPage/interCompany/helpers';
import { LockStatus } from 'pages/AuthorizePaymentPage/types';
import { updateActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/slice';
import { PROGRESS_BAR_WIDTH } from 'pages/VendorsTablePage/components/dialogs';
import {
  clearCheckWritingResponse,
  clearPostSelectedChecks,
  getChecksFailedFormatted,
  getCheckWritingResponseSuccess,
  getPaymentDate,
  getPostingDescription,
  getPrintedCheckWithArchiveError,
  getPrintedCheckWithGLError,
  getPrintedCheckWithCheckFormError,
  getPrintedOKChecks,
  getPrintOrder,
  getSelectedCIBAccount,
  getSelectedVendors,
  getToVoidChecks,
  getUnlockSuccessfulInvoicesPayload,
  removeFromPrintOKList,
  removeFromSelectedVendors,
  setCheckWritingResponse,
  setIsPrintChecksDialogOpen,
  setIsPrintFailureDialogOpen,
  setIsPrintSuccessDialogOpen,
  getManualCheckDetails,
  setInterCompanyCIBAccount,
  getInterCompanyCIBAccount
} from 'pages/VendorsTablePage/components/PrintChecks';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import { errors, labels } from './messages';
import { PrintSuccessDialogContent } from './PrintSuccessDialogContent';
import { StyledDialog, StyledDiv, StyledToast } from './styled';
import { PrintSuccessDialogProps } from './types';
import { useGetCheckNumbersList } from './useGetCheckNumbersList';

export const PrintSuccessDialog = ({
  open,
  journalStoreId,
  journalNumber,
  cashInBankAccount,
  postingDesc,
  journalName,
  journalCompanyId,
  cashInBankStoreId,
  cashCompanyId
}: PrintSuccessDialogProps): React.JSX.Element => {
  const amplitude = useAmplitude();
  const intl = useIntl();
  const client = useClient();
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const [shouldSendRequestToVoidChecks, setShouldSendRequestToVoidChecks] =
    useState<boolean>(false);
  const [shouldShowVoidFailureToast, setShouldShowVoidFailureToast] =
    useState<boolean>(false);
  const [isVoidAndReprint, setIsVoidAndReprint] = useState<boolean>(false);
  const [isReprintLoading, setIsReprintLoading] = useState<boolean>(false);
  const [failedVoidChecks, setFailedVoidChecks] = useState<Array<string>>([]);
  const [partiallyPrintedErrorMessage, setPartiallyPrintedErrorMessage] =
    useState<string>('');
  const toVoidChecksInfo = useAppSelector(getToVoidChecks);
  const toVoidChecks = useGetCheckNumbersList(toVoidChecksInfo);
  const companyId = useAppSelector(getCompanyId);
  const printedOKChecks = useAppSelector(getPrintedOKChecks);
  const unlockInvoicesPayload = useAppSelector(
    getUnlockSuccessfulInvoicesPayload
  );

  const { mutate: deleteApprovals } = useDeleteApproval() || {};
  const printedChecksWithArchiveError = useAppSelector(
    getPrintedCheckWithArchiveError
  );
  const { data: cibAccountData } = useFetchCashInBankInterCompany(false);
  const cashCompanyStoreid = useMemo(
    () => getStoreIdByCompanyId(cibAccountData?.cashCompanies, cashCompanyId),
    [cibAccountData?.cashCompanies, cashCompanyId]
  );
  const interCompanyCIBAccount = useAppSelector(getInterCompanyCIBAccount);
  const { refetch } = useFetchPrintersWithCheckNo(
    interCompanyCIBAccount?.accountNumber,
    cashCompanyStoreid
  );

  const printedChecksWithGLError = useAppSelector(getPrintedCheckWithGLError);

  const printedChecksWithCheckFormError = useAppSelector(
    getPrintedCheckWithCheckFormError
  );

  const { mutate: updateLockStatus, isLoading: isUpdateLockStatusLoading } =
    useUpdateInvoiceLockStatus();

  const {
    error: voidError,
    isLoading: isVoidLoading,
    data: voidQueryResult,
    mutate: voidChecksApi
  } = useVoidChecks() || {};
  const selectedVendors = useAppSelector(getSelectedVendors);
  const paymentDate = useAppSelector(getPaymentDate);
  const postingDescription = useAppSelector(getPostingDescription);
  const printOrder = useAppSelector(getPrintOrder);
  const { accountNumber } = useAppSelector(getSelectedCIBAccount);
  const postSelectedWorkFlow = printedOKChecks.length > 0;
  const storeId = useAppSelector(getStoreId);
  const successfullChecks = useAppSelector(getCheckWritingResponseSuccess);
  const failedChecks = useAppSelector(getChecksFailedFormatted);
  const manualCheckDetailsPayload = useAppSelector(getManualCheckDetails);
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');
  const checkSequencingFlag = useFlagValue('dmsAccountingCheckSequencing');

  useEffect(() => {
    let message = '';
    if (printedChecksWithArchiveError.length)
      message = intl.formatMessage(errors.archiveError, {
        checkNumber: printedChecksWithArchiveError
          .map(({ checkNumber }) => checkNumber)
          .join(',')
      });
    if (printedChecksWithCheckFormError.length)
      message = intl.formatMessage(errors.checkFormRenderError);
    if (printedChecksWithGLError.length)
      message = intl.formatMessage(errors.glError);
    setPartiallyPrintedErrorMessage(message);
  }, [
    intl,
    printedChecksWithArchiveError,
    printedChecksWithGLError,
    printedChecksWithCheckFormError
  ]);

  useEffect(() => {
    /* eslint-disable react-hooks/exhaustive-deps */
    if (successfullChecks.length)
      amplitudeTracker(amplitude, EVENT_NAME.PrintChecksSuccess, {
        'Successful Print': successfullChecks.length
      });
  }, [successfullChecks]);

  useEffect(() => {
    if (voidError || failedVoidChecks.length > 0)
      setShouldShowVoidFailureToast(true);
    else setShouldShowVoidFailureToast(false);
  }, [voidError, failedVoidChecks.length]);

  useEffect(() => {
    if (successfullChecks.length === 0) {
      dispatch(setIsPrintSuccessDialogOpen(false));
      dispatch(setIsPrintChecksDialogOpen(false));
      if (failedChecks.length > 0) dispatch(setIsPrintFailureDialogOpen(true));
      else {
        dispatch(clearCheckWritingResponse());
      }
    }
  }, [
    successfullChecks,
    failedChecks,
    successfullChecks.length,
    failedChecks.length,
    dispatch
  ]);

  const printReqObj = {
    paymentDate,
    postingDescription: interCompanyFlag ? postingDesc : postingDescription,
    printOrder,
    cashInBankAccount: interCompanyFlag ? cashInBankAccount : accountNumber,
    vendors: selectedVendors,
    ...(interCompanyFlag && {
      journalNumber,
      journalStoreId,
      journalName,
      journalCompanyId,
      cashInBankStoreId,
      cashCompanyId
    }),
    ...(checkSequencingFlag && {
      dmsPrinterNo: manualCheckDetailsPayload.dmsPrinterNo || null,
      printerId: manualCheckDetailsPayload.printerId || null,
      archiveOnly: manualCheckDetailsPayload.archiveOnly,
      manualEntryCheckSequence:
        manualCheckDetailsPayload.manualCheckNumber &&
        manualCheckDetailsPayload.isSequenceForFutureChecks !== null
          ? {
              checkNumber: manualCheckDetailsPayload.manualCheckNumber,
              useSequenceForFutureChecks:
                manualCheckDetailsPayload.isSequenceForFutureChecks
            }
          : null
    })
  };

  const handlePostSelected = () => {
    if (isUpdateLockStatusLoading) return;
    amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
      'Post Selected Checks': unlockInvoicesPayload.length
    });
    updateLockStatus(
      {
        lockStatus: LockStatus.UNLOCKED,
        invoices: unlockInvoicesPayload
      },
      {
        onSuccess: ({ successes }) => {
          if (!flags.dmsAccountingLandingPage) {
            deleteApprovals(successes);
          }
          if (successfullChecks.length === printedOKChecks.length) {
            dispatch(updateActiveAuthorizePaymentPage(''));
            dispatch(
              setInterCompanyCIBAccount({
                companyId: '',
                accountName: '',
                accountNumber: ''
              })
            );
          }
          dispatch(
            removeFromSelectedVendors(successes.map(({ vendorNo }) => vendorNo))
          );
          dispatch(clearPostSelectedChecks());
        }
      }
    );
  };

  const handleSuccessfullyVoidChecks = ({ vendorNo }: CheckInfoType): void => {
    dispatch(removeFromSelectedVendors([vendorNo]));
    dispatch(removeFromPrintOKList(vendorNo));
    dispatch(updateActiveAuthorizePaymentPage(''));
    dispatch(
      setInterCompanyCIBAccount({
        companyId: '',
        accountName: '',
        accountNumber: ''
      })
    );
  };

  useEffect(() => {
    if (voidQueryResult) {
      setShouldSendRequestToVoidChecks(false);
      voidQueryResult.forEach(checkStatus => {
        if (isEmpty(checkStatus.error)) {
          const successfullyVoidCheck = toVoidChecksInfo.find(
            ({ checkNumber }) => checkNumber === checkStatus.checkNumber
          );
          if (
            successfullyVoidCheck &&
            isVoidAndReprint &&
            checkSequencingFlag
          ) {
            dispatch(setIsPrintSuccessDialogOpen(false));
            refetch();
          } else if (
            successfullyVoidCheck &&
            !isVoidAndReprint &&
            checkSequencingFlag
          ) {
            handleSuccessfullyVoidChecks(successfullyVoidCheck);
          } else if (successfullyVoidCheck && !checkSequencingFlag) {
            handleSuccessfullyVoidChecks(successfullyVoidCheck);
          }
        } else {
          setFailedVoidChecks(previousState => [
            ...previousState,
            checkStatus.checkNumber
          ]);
        }
      });
    }
  }, [voidQueryResult]);

  const showLoader =
    isReprintLoading ||
    isUpdateLockStatusLoading ||
    (isVoidLoading && shouldSendRequestToVoidChecks);

  const handleVoidFailureMessageDismiss = () => {
    setFailedVoidChecks([]);
    setShouldSendRequestToVoidChecks(false);
    if (failedChecks.length) {
      dispatch(setIsPrintSuccessDialogOpen(false));
      dispatch(setIsPrintFailureDialogOpen(true));
    }
    setShouldShowVoidFailureToast(false);
  };

  const handleRetry = () => {
    setFailedVoidChecks([]);

    const voidReqObj = {
      checks: toVoidChecks,
      ...(interCompanyFlag && { journalNumber, journalStoreId, cashCompanyId })
    };
    setShouldSendRequestToVoidChecks(true);
    voidChecksApi(voidReqObj);
  };

  const handleVoid = () => {
    if (isVoidLoading) return;
    setIsVoidAndReprint(false);
    const voidReqObj = {
      checks: toVoidChecks,
      ...(interCompanyFlag && { journalNumber, journalStoreId, cashCompanyId })
    };
    if (toVoidChecks.length > 0) {
      setShouldSendRequestToVoidChecks(true);
      voidChecksApi(voidReqObj);
      amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
        'Void Printed Checks': toVoidChecks.length
      });
    }
  };

  const handleReprintResponse = (res: any) => {
    setIsReprintLoading(false);
    if (isArray(res) && res.some(({ error }) => error !== '')) {
      res.forEach(check => {
        if (check.error !== '')
          setFailedVoidChecks(previousState => [
            ...previousState,
            check.checkNumber
          ]);
      });
    }
    if (!isArray(res)) {
      dispatch(setCheckWritingResponse(res));
    }
  };

  const handleVoidAndReprint = () => {
    if (isReprintLoading || isVoidLoading) return;
    setIsVoidAndReprint(true);
    const voidReqObj = {
      checks: toVoidChecks,
      ...(interCompanyFlag && { journalNumber, journalStoreId, cashCompanyId })
    };
    /* eslint-disable react-hooks/exhaustive-deps */
    if (voidReqObj.checks.length && printReqObj.vendors.length)
      amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
        'Void and Reprint Checks':
          voidReqObj.checks.length + printReqObj.vendors.length
      });
    if (!client) return;
    if (checkSequencingFlag) {
      if (toVoidChecks.length > 0) {
        setShouldSendRequestToVoidChecks(true);
        voidChecksApi(voidReqObj);
        amplitudeTracker(amplitude, EVENT_NAME.PrintChecks, {
          'Void Printed Checks': toVoidChecks.length
        });
      }
    } else {
      setIsReprintLoading(true);
      voidAndReprintChecks(
        client.headers,
        companyId,
        storeId,
        voidReqObj,
        printReqObj
      )
        .then(handleReprintResponse)
        .catch(err => {
          setShouldShowVoidFailureToast(true);
          setIsReprintLoading(false);
          console.error(err);
        });
    }
  };

  const buttonsForDialog = [
    {
      id: 'void',
      onClick: () => handleVoid(),
      text: intl.formatMessage(labels.void),
      isDisabled: postSelectedWorkFlow,
      variant: 'text'
    },
    {
      id: 'void-reprint',
      onClick: () => handleVoidAndReprint(),
      isDisabled: postSelectedWorkFlow,
      text: intl.formatMessage(labels.voidReprint),
      variant: 'text'
    }
  ];

  const leftButtonProps = {
    id: 'post-selected',
    onClick: handlePostSelected,
    isDisabled: !postSelectedWorkFlow,
    text: intl.formatMessage(labels.postSelected),
    variant: 'text'
  };

  return (
    <StyledDialog
      buttonsProps={buttonsForDialog}
      leftButtonProps={leftButtonProps}
      disableOverlayClicked
      hideCloseButton
      title={
        <FormattedMessage
          defaultMessage="Print Checks"
          description="print success title"
        />
      }
      open={open}
      dataTestId="print-success-dialog"
      id="print-success-dialog"
    >
      {showLoader && (
        <IndeterminateProgressBar
          dataTestId="success-dialog-loader"
          label=""
          hideLabel
          trackWidth={PROGRESS_BAR_WIDTH}
        />
      )}
      <StyledDiv>
        {shouldShowVoidFailureToast && (
          <StyledToast>
            <Toast
              content={intl.formatMessage(errors.voidError, {
                checkNumber: failedVoidChecks.join(',')
              })}
              buttonProps={[
                {
                  text: intl.formatMessage(labels.retry),
                  onClick: () => handleRetry()
                },
                {
                  text: intl.formatMessage(labels.dismiss),
                  onClick: () => handleVoidFailureMessageDismiss()
                }
              ]}
            />
          </StyledToast>
        )}
        {partiallyPrintedErrorMessage && (
          <StyledToast>
            <Toast
              content={partiallyPrintedErrorMessage}
              buttonProps={[
                {
                  text: intl.formatMessage(labels.dismiss),
                  onClick: () => setPartiallyPrintedErrorMessage('')
                }
              ]}
            />
          </StyledToast>
        )}
        <PrintSuccessDialogContent />
      </StyledDiv>
    </StyledDialog>
  );
};
